/**
 * Deterministic "last ordered" social proof generator.
 * Based on product ID + UTC time bucket, so ALL visitors on ALL devices see
 * the SAME message at the same moment. Uses no real customer data — DSGVO-safe.
 *
 * Key design:
 *  - A deterministic "order timestamp" is computed per product+bucket.
 *  - The "X minutes ago" label is derived live from (now - orderTimestamp),
 *    so refreshing 5 min later genuinely shows 5 min more elapsed time.
 *  - Cities are weighted: bigger/well-known Swiss cities appear far more often.
 *  - 100% client-side, zero database calls — no Cloud credits consumed.
 */

// Schweizer Städte mit Gewichtung (höhere Zahl = häufigeres Erscheinen).
// Grosse, bekannte Städte dominieren — kleinere Orte sind nur selten dabei.
const CH_CITIES: Array<[string, number]> = [
  // Top-Tier (sehr häufig) — die grössten/bekanntesten CH-Städte
  ['Zürich', 30], ['Genf', 22], ['Basel', 20], ['Bern', 18], ['Lausanne', 16],
  ['Winterthur', 12], ['Luzern', 12], ['St. Gallen', 10], ['Lugano', 9], ['Biel', 8],
  // Mittlere Städte (regelmässig)
  ['Thun', 6], ['Schaffhausen', 6], ['Freiburg', 6], ['Chur', 6], ['Neuchâtel', 6],
  ['Sion', 5], ['Zug', 5], ['Aarau', 5], ['Baden', 5], ['Solothurn', 5],
  ['Bellinzona', 4], ['Locarno', 4], ['Montreux', 4], ['Vevey', 4], ['Nyon', 4],
  ['Uster', 4], ['Dübendorf', 4], ['Rapperswil-Jona', 4], ['Kreuzlingen', 4], ['Wil', 4],
  ['Olten', 4], ['Wetzikon', 3], ['Frauenfeld', 3], ['Liestal', 3], ['Burgdorf', 3],
  // Kleinere Städte (gelegentlich)
  ['Köniz', 2], ['La Chaux-de-Fonds', 2], ['Vernier', 2], ['Lancy', 2], ['Emmen', 2],
  ['Kriens', 2], ['Dietikon', 2], ['Allschwil', 2], ['Renens', 2], ['Riehen', 2],
  ['Wettingen', 2], ['Carouge', 2], ['Onex', 2], ['Kloten', 2], ['Meyrin', 2],
  ['Horgen', 2], ['Wädenswil', 2], ['Adliswil', 2], ['Bülach', 2], ['Schlieren', 2],
  ['Pratteln', 2], ['Wallisellen', 2], ['Muttenz', 2], ['Volketswil', 2], ['Gossau', 2],
  ['Yverdon-les-Bains', 2], ['Bulle', 2], ['Sierre', 2], ['Martigny', 2], ['Brig-Glis', 2],
  ['Davos', 2], ['Interlaken', 2], ['Langenthal', 2], ['Herisau', 2], ['Sursee', 2],
  // Selten (kleine Orte) — Long Tail
  ['Baar', 1], ['Pully', 1], ['Thônex', 1], ['Regensdorf', 1], ['Münchenstein', 1],
  ['Uzwil', 1], ['Reinach', 1], ['Thalwil', 1], ['Steffisburg', 1], ['Zollikon', 1],
  ['Lyss', 1], ['Morges', 1], ['Wohlen', 1], ['Stäfa', 1], ['Küsnacht', 1],
  ['Meilen', 1], ['Spiez', 1], ['Naters', 1], ['Romanshorn', 1], ['Ostermundigen', 1],
];

// Österreichische Städte (Phase 7-Erweiterung, bewusst sichtbare AT-Änderung).
const AT_CITIES: Array<[string, number]> = [
  ['Wien', 30], ['Graz', 18], ['Linz', 16], ['Salzburg', 14], ['Innsbruck', 12],
  ['Klagenfurt', 10], ['Villach', 8], ['Wels', 7], ['St. Pölten', 7], ['Dornbirn', 6],
  ['Bregenz', 5], ['Steyr', 5], ['Feldkirch', 4], ['Leonding', 4], ['Krems', 4],
  ['Baden', 3], ['Wolfsberg', 3], ['Kufstein', 3], ['Traun', 3], ['Amstetten', 3],
];

// Italienische Städte (Phase 7 IT-Storefront).
const IT_CITIES: Array<[string, number]> = [
  ['Milano', 30], ['Roma', 28], ['Napoli', 20], ['Torino', 18], ['Bologna', 14],
  ['Firenze', 13], ['Genova', 12], ['Verona', 10], ['Padova', 10], ['Brescia', 9],
  ['Palermo', 9], ['Bari', 8], ['Catania', 7], ['Venezia', 7], ['Modena', 6],
  ['Parma', 5], ['Trieste', 5], ['Bergamo', 5], ['Reggio Emilia', 4], ['Perugia', 4],
];

// Albanischsprachiger Raum (Phase 8 AL-Storefront): Albanien, Kosovo und
// Nordmazedonien — der reale Markt der .al-Storefront.
const AL_CITIES: Array<[string, number]> = [
  // Albanien
  ['Tirana', 30], ['Durrës', 18], ['Vlorë', 14], ['Shkodër', 12], ['Elbasan', 10],
  ['Fier', 8], ['Korçë', 7], ['Berat', 6],
  // Kosovo
  ['Prishtina', 20], ['Prizren', 12], ['Peja', 8], ['Gjakova', 7], ['Ferizaj', 6],
  ['Mitrovica', 5],
  // Nordmazedonien
  ['Tetovo', 7], ['Gostivar', 5], ['Struga', 4], ['Kumanovo', 4], ['Debar', 3],
];

type StorefrontKey = 'ch' | 'at' | 'it' | 'al' | (string & {});

/**
 * LISTENGESTEUERT: Storefront #5 braucht genau einen Eintrag in dieser Map
 * (plus optional eine eigene Städteliste). Kein `=== 'xx'` mehr im Code.
 * Fehlt ein Eintrag, greift der CH-Pool als Fallback.
 */
const STOREFRONT_CITIES: Record<string, Array<[string, number]>> = {
  ch: CH_CITIES,
  de: CH_CITIES,
  at: AT_CITIES,
  it: IT_CITIES,
  al: AL_CITIES,
};

function citiesFor(sf?: StorefrontKey): Array<[string, number]> {
  return (sf && STOREFRONT_CITIES[sf]) || CH_CITIES;
}

function pickWeightedCity(seed: number, sf?: StorefrontKey): string {
  const pool = citiesFor(sf);
  const total = pool.reduce((s, [, w]) => s + w, 0);
  const target = seed % total;
  let acc = 0;
  for (const [name, w] of pool) {
    acc += w;
    if (target < acc) return name;
  }
  return pool[0][0];
}

function hashString(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export interface LastOrderInfo {
  city: string;
  /** Localized "ago" label, e.g. "vor 2 Std." */
  agoLabel: string;
  /** UTC timestamp (ms) when the simulated order happened — useful for live re-rendering. */
  orderTimestamp: number;
}

export type SocialProofTier = 'topseller' | 'popular' | 'medium' | 'low' | 'niche';

export interface SocialProofTierInfo {
  key: SocialProofTier;
  label: string;
  /** German short range, e.g. "2–90 Min" */
  range: string;
  /** Tailwind classes for badge */
  badgeClass: string;
}

/**
 * Returns the tier classification based on REAL orders in the last 30 days.
 * Live, dynamic categorization — no hardcoded baselines.
 */
export function getSocialProofTier(ordersLast30Days: number): SocialProofTierInfo {
  if (ordersLast30Days >= 100) {
    return { key: 'topseller', label: '🔥 TopSeller', range: '2–90 Min', badgeClass: 'bg-red-500/10 text-red-600 border-red-500/20' };
  }
  if (ordersLast30Days >= 40) {
    return { key: 'popular', label: '⭐ Beliebt', range: '5 Min – 3 Std', badgeClass: 'bg-orange-500/10 text-orange-600 border-orange-500/20' };
  }
  if (ordersLast30Days >= 15) {
    return { key: 'medium', label: '✓ Mittel', range: '30 Min – 6 Std', badgeClass: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' };
  }
  if (ordersLast30Days >= 5) {
    return { key: 'low', label: '· Selten', range: '1–12 Std', badgeClass: 'bg-sky-500/10 text-sky-600 border-sky-500/20' };
  }
  return { key: 'niche', label: '○ Nische', range: '2–48 Std', badgeClass: 'bg-muted text-muted-foreground border-border' };
}

/**
 * Returns a deterministic last-order display for a product.
 *
 * IMPORTANT: every visitor on every device sees the SAME order timestamp at
 * the same moment in time, because the bucket is derived from UTC and the
 * product id only — no random, no per-session state.
 *
 * The age label is computed LIVE from (now - orderTimestamp), so refreshing
 * 5 min later truly shows 5 min more elapsed time, until the next bucket
 * rotation picks a fresh order.
 *
 * Bucket length scales with 30-day order volume so high-traffic products
 * rotate more frequently than niche ones (which would feel stale otherwise).
 */
export function getLastOrder(
  productId: string,
  productSlug: string,
  locale: string = 'de',
  ordersLast7Days: number = 0,
  nowMs: number = Date.now(),
  storefront?: StorefrontKey,
): LastOrderInfo | null {
  // d7-getriebene Tiers mit Hard-Caps auf die maximale sichtbare "vor …"-Zeit.
  // d7 = 0 → keine Zeile (Caller blendet aus). Verhindert unplausible
  // Aussagen wie "vor 1 Tag" bei einem TopSeller.
  if (ordersLast7Days <= 0) return null;

  let bucketMinutes: number;
  let minMinutes: number;
  let maxMinutes: number;

  if (ordersLast7Days >= 25)      { bucketMinutes = 45;   minMinutes = 2;   maxMinutes = 240; }   // TopSeller: bis 4h
  else if (ordersLast7Days >= 10) { bucketMinutes = 120;  minMinutes = 10;  maxMinutes = 480; }   // Hot: bis 8h
  else if (ordersLast7Days >= 3)  { bucketMinutes = 360;  minMinutes = 30;  maxMinutes = 1440; }  // Mittel: bis 24h
  else                             { bucketMinutes = 720;  minMinutes = 120; maxMinutes = 2880; } // Langsam: bis 48h

  const bucketMs = bucketMinutes * 60 * 1000;
  const bucketIndex = Math.floor(nowMs / bucketMs);
  const bucketStartMs = bucketIndex * bucketMs;

  const seed = hashString(`${productId}|${productSlug}|${bucketIndex}`);

  const city = pickWeightedCity(seed, storefront);

  // Pick a stable "minutes-ago-at-bucket-start" value. As real time passes
  // within the bucket, the live age (now - orderTimestamp) grows naturally.
  const range = maxMinutes - minMinutes;
  const minutesAgoAtBucketStart = minMinutes + (seed % range);
  const orderTimestamp = bucketStartMs - minutesAgoAtBucketStart * 60 * 1000;

  const liveMinutesAgo = Math.max(0, Math.round((nowMs - orderTimestamp) / 60000));
  const agoLabel = formatAgo(liveMinutesAgo, locale);

  return { city, agoLabel, orderTimestamp };
}

function formatAgo(minutes: number, locale: string): string {
  const labels: Record<string, { minAgo: (n: number) => string; hourAgo: (n: number) => string; dayAgo: (n: number) => string; justNow: string }> = {
    de: {
      justNow: 'gerade eben',
      minAgo: (n) => `vor ${n} Min.`,
      hourAgo: (n) => `vor ${n} ${n === 1 ? 'Std.' : 'Std.'}`,
      dayAgo: (n) => `vor ${n} ${n === 1 ? 'Tag' : 'Tagen'}`,
    },
    en: {
      justNow: 'just now',
      minAgo: (n) => `${n} min ago`,
      hourAgo: (n) => `${n} ${n === 1 ? 'hour' : 'hours'} ago`,
      dayAgo: (n) => `${n} ${n === 1 ? 'day' : 'days'} ago`,
    },
    fr: {
      justNow: "à l'instant",
      minAgo: (n) => `il y a ${n} min`,
      hourAgo: (n) => `il y a ${n} h`,
      dayAgo: (n) => `il y a ${n} ${n === 1 ? 'jour' : 'jours'}`,
    },
    it: {
      justNow: 'poco fa',
      minAgo: (n) => `${n} min fa`,
      hourAgo: (n) => `${n} ${n === 1 ? 'ora' : 'ore'} fa`,
      dayAgo: (n) => `${n} ${n === 1 ? 'giorno' : 'giorni'} fa`,
    },
    sq: {
      justNow: 'sapo tani',
      minAgo: (n) => `para ${n} min`,
      hourAgo: (n) => `para ${n} ${n === 1 ? 'ore' : 'orësh'}`,
      dayAgo: (n) => `para ${n} ${n === 1 ? 'dite' : 'ditësh'}`,
    },
  };

  const l = labels[locale] || labels.de;
  if (minutes < 2) return l.justNow;
  if (minutes < 60) return l.minAgo(minutes);
  if (minutes < 24 * 60) {
    const hours = Math.round(minutes / 60);
    return l.hourAgo(hours);
  }
  const days = Math.round(minutes / (60 * 24));
  return l.dayAgo(days);
}